import { debounce } from 'lodash';

import { commonOverlayOptions } from 'helpers/research/map_constants';

function getDefaultOptions(google) {
  return {
    drawingControl: true,
    drawingControlOptions: {
      position: google.maps.ControlPosition.TOP_LEFT,
      drawingModes: ['circle', 'rectangle', 'polygon']
    },
    circleOptions: commonOverlayOptions,
    polygonOptions: commonOverlayOptions,
    rectangleOptions: commonOverlayOptions
  };
}

export default class PropertiesMapDrawingManager {
  constructor(google, map, { onBoundsChange } = {}) {
    this.google = google;
    this.drawingManager = new google.maps.drawing.DrawingManager(getDefaultOptions(google));
    this.drawingManager.setMap(map);

    this.removeDrawingControl = this.createRemoveDrawingControl(google, map);

    if (onBoundsChange) {
      this.onBoundsChange = onBoundsChange;
      this.debouncedOnBoundsChange = debounce(onBoundsChange, 100);
    } else {
      this.onBoundsChange = () => {};
      this.debouncedOnBoundsChange = () => {};
    }

    // No click events for the drawing controls, unfortunately
    window.addEventListener('mouseup', this.handleDrawStart);
    google.maps.event.addListener(this.drawingManager, 'overlaycomplete', this.handleOverlayComplete);
  }

  createRemoveDrawingControl(google, map) {
    const control = document.createElement('button');

    control.setAttribute('class', 'drawing-remove-control hidden');
    control.innerHTML = 'Remove drawing';

    control.addEventListener('click', () => this.deleteOverlay());

    map.controls[google.maps.ControlPosition.TOP_LEFT].push(control);

    return control;
  }

  showRemoveDrawingControl() {
    this.removeDrawingControl.classList.remove('hidden');
  }

  hideRemoveDrawingControl() {
    this.removeDrawingControl.classList.add('hidden');
  }

  getBounds() {
    switch (this.overlayType) {
    case 'circle':
      return {
        center: getLatLng(this.overlay.center),
        radius: this.overlay.radius,
        radiusUnits: 'm',
        type: this.overlayType
      };
    case 'polygon':
      return {
        points: this.overlay.getPath().getArray().map(getLatLng),
        type: this.overlayType
      };
    case 'rectangle':
      return {
        northEast: getLatLng(this.overlay.getBounds().getNorthEast()),
        southWest: getLatLng(this.overlay.getBounds().getSouthWest()),
        type: this.overlayType
      };
    default:
      return null;
    }
  }

  deleteOverlay() {
    if (this.overlay) {
      this.hideRemoveDrawingControl();
      this.overlay.setMap(null);
      this.overlay = null;
      this.overlayType = null;
      this.onBoundsChange(this.getBounds());
    }
  }

  handleDrawStart = () => {
    setTimeout(() => {
      if (this.drawingManager.getDrawingMode()) {
        this.deleteOverlay();
      }
    });
  }

  handleOverlayComplete = (event) => {
    if (this.overlay) {
      this.deleteOverlay();
    }

    this.overlay = event.overlay;
    this.overlayType = event.type;

    this.showRemoveDrawingControl();

    this.drawingManager.setDrawingMode(null);
    this.onBoundsChange(this.getBounds());

    switch (this.overlayType) {
    case 'circle':
      for (const eventType of ['center_changed', 'radius_changed']) {
        this.google.maps.event.addListener(this.overlay, eventType, this.handleOverlayChange);
      }
      break;
    case 'polygon':
      this.google.maps.event.addListener(this.overlay, 'dragend', this.handleOverlayChange);
      for (const eventType of ['insert_at', 'remove_at', 'set_at']) {
        this.google.maps.event.addListener(this.overlay.getPath(), eventType, this.handleOverlayChange);
      }
      break;
    case 'rectangle':
      this.google.maps.event.addListener(this.overlay, 'bounds_changed', this.handleOverlayChange);
      break;
    }
  }

  handleOverlayChange = () => {
    this.debouncedOnBoundsChange(this.getBounds());
  }
}

function getLatLng(point) {
  return {
    lat: point.lat(),
    lng: point.lng()
  };
}
